import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import tweaqLogo from "@/assets/tweaq-logo.svg";
import { useLangPath } from "@/hooks/use-lang-path";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const lp = useLangPath();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const navItems = [
    {
      label: t("nav.solutions"),
      href: "#",
      dropdown: [
        { label: t("nav.sim"), sub: t("nav.simSub"), href: lp("/solutions/sim") },
        { label: t("nav.ops"), sub: t("nav.opsSub"), href: lp("/solutions/ops") },
      ],
    },
    {
      label: t("nav.industries"),
      href: "#",
      dropdown: [
        { label: t("nav.logistics"), href: lp("/industries/logistics") },
        { label: t("nav.buildings"), href: lp("/industries/buildings") },
        { label: t("nav.manufacturing"), href: lp("/industries/manufacturing") },
      ],
    },
    {
      label: t("nav.about"),
      href: lp("/about"),
      dropdown: [
        { label: t("nav.aboutUs"), href: lp("/about") },
        { label: t("nav.careers"), href: lp("/careers") },
      ],
    },
    { label: t("nav.contact"), href: lp("/contact") },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);

  const switchLang = (lng: string) => {
    i18n.changeLanguage(lng);
    // Navigate to the same page in the new language
    const newPath = location.pathname.replace(/^\/(en|nl)/, `/${lng}`);
    navigate(newPath || `/${lng}`);
    setLangOpen(false);
  };

  const currentLang = i18n.language === "nl" ? "NL" : "EN";

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-sm border-b border-border" : "bg-transparent"} ${open ? "md:bg-background/95 md:backdrop-blur-sm md:border-b md:border-border" : ""}`}>
        <div className="container-narrow flex items-center justify-between h-20 px-6">
          <Link to={lp("/")}>
            <img src={tweaqLogo} alt="tweaq" className="h-11 transition-all duration-300" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.dropdown && handleMouseEnter(item.label)}
                onMouseLeave={() => item.dropdown && handleMouseLeave()}
              >
                {item.dropdown ? (
                  <button className="flex items-center gap-1 text-base text-muted-foreground hover:text-foreground transition-colors">
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <Link to={item.href} className="text-base text-muted-foreground hover:text-foreground transition-colors">
                    {item.label}
                  </Link>
                )}

                {item.dropdown && activeDropdown === item.label && (
                  <div className="absolute top-full left-0 pt-2">
                    <div className="bg-background border border-border rounded-lg shadow-lg py-2 min-w-[300px]">
                      {item.dropdown.map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          className="block px-4 py-2.5 hover:bg-muted transition-colors"
                        >
                          <span className="text-base text-foreground font-medium">
                            {sub.label}
                            {"sub" in sub && sub.sub && (
                              <span className="text-muted-foreground font-normal"> — {sub.sub}</span>
                            )}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Language Switcher */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Globe className="w-4 h-4" />
                {currentLang}
                <ChevronDown className={`w-3 h-3 transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 pt-2">
                  <div className="bg-background border border-border rounded-lg shadow-lg py-1 min-w-[80px]">
                    <button
                      onClick={() => switchLang("en")}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${i18n.language === "en" ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => switchLang("nl")}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${i18n.language === "nl" ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      NL
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Link to={lp("/contact")}>
              <Button size="sm">{t("nav.bookSession")}</Button>
            </Link>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={() => switchLang(i18n.language === "en" ? "nl" : "en")}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Globe className="w-4 h-4" />
              {currentLang}
            </button>
            <button
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="text-foreground"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile fullscreen overlay */}
      {open && (
        <>
          <div className="fixed inset-0 z-[55] bg-black/40 md:hidden" onClick={() => setOpen(false)} />
          <div className="fixed top-0 left-0 right-0 z-[60] bg-background md:hidden shadow-lg">
            <div className="flex items-center justify-between h-20 px-6 border-b border-border">
              <Link to={lp("/")} onClick={() => setOpen(false)}>
                <img src={tweaqLogo} alt="tweaq" className="h-11" />
              </Link>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X className="w-6 h-6 text-foreground" />
              </button>
            </div>
            <div className="px-6 py-6 space-y-1">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.dropdown ? (
                    <>
                      <button
                        className="flex items-center justify-between w-full py-3 text-foreground text-base"
                        onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      >
                        {item.label}
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                      </button>
                      {mobileExpanded === item.label && (
                        <div className="pl-4 space-y-1 mb-2">
                          {item.dropdown.map((sub) => (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              className="block py-2 text-base text-muted-foreground hover:text-foreground"
                              onClick={() => setOpen(false)}
                            >
                              {sub.label}
                              {"sub" in sub && sub.sub && (
                                <span className="text-muted-foreground/70 font-normal"> — {sub.sub}</span>
                              )}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className="block py-3 text-foreground text-base"
                      onClick={() => setOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile Language Switcher */}
              <div className="flex items-center gap-3 py-3 border-t border-border mt-2 pt-4">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <button
                  onClick={() => switchLang("en")}
                  className={`text-sm px-2 py-1 rounded ${i18n.language === "en" ? "text-foreground font-medium bg-muted" : "text-muted-foreground"}`}
                >
                  EN
                </button>
                <button
                  onClick={() => switchLang("nl")}
                  className={`text-sm px-2 py-1 rounded ${i18n.language === "nl" ? "text-foreground font-medium bg-muted" : "text-muted-foreground"}`}
                >
                  NL
                </button>
              </div>

              <Link to={lp("/contact")} onClick={() => setOpen(false)}>
                <Button size="sm" className="w-full mt-4">{t("nav.bookSession")}</Button>
              </Link>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
